import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../Routes/AuthProvider";
import { GetAllReservationsCount } from "../Services/ReservationsApi";
import { getAllHotelBookings } from "../Services/HotelSearchApi";
import HeaderDashboard from "../Components/HeaderDashboard";

const Dashboard = () => {
	const { userName } = useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [counts, setCounts] = useState({
		flightCounts: 0,
		hotelCounts: 0,
		busCounts: 0,
		cabCounts: 0,
		visaCounts: 0,
		forexCounts: 0,
		insuranceCounts: 0,
		trainCounts: 0,
	});
	const [hotelBookings, setHotelBookings] = useState([]);

	useEffect(() => {
		const fetchDashboardData = async () => {
			setLoading(true);
			try {
				// 1. Fetch counts
				const response = await GetAllReservationsCount(userName);
				//console.log("counts", response);
				setCounts({
					flightCounts: response.flightCounts ?? 0,
					hotelCounts: response.hotelCounts ?? 0,
					busCounts: response.busCounts ?? 0,
					cabCounts: response.cabCounts ?? 0,
					visaCounts: response.visaCounts ?? 0,
					forexCounts: response.forexCounts ?? 0,
					insuranceCounts: response.insuranceCounts ?? 0,
					trainCounts: response.trainCounts ?? 0,
				});

				// 2. Fetch hotel bookings for day-wise charts
				const bookingsData = await getAllHotelBookings(userName);
				//console.log("bookingsData", bookingsData);
				let list = [];
				if (Array.isArray(bookingsData)) {
					list = bookingsData;
				} else if (bookingsData?.bookings && Array.isArray(bookingsData.bookings)) {
					list = bookingsData.bookings;
				} else if (bookingsData?.data?.allHotelBookings && Array.isArray(bookingsData.data.allHotelBookings)) {
					list = bookingsData.data.allHotelBookings;
				} else if (bookingsData?.data && Array.isArray(bookingsData.data)) {
					list = bookingsData.data;
				} else if (bookingsData?.allTransactions && Array.isArray(bookingsData.allTransactions)) {
					list = bookingsData.allTransactions;
				} else if (bookingsData?.allHotelBookings && Array.isArray(bookingsData.allHotelBookings)) {
					list = bookingsData.allHotelBookings;
				} else if (bookingsData?.data?.allTransactions && Array.isArray(bookingsData.data.allTransactions)) {
					list = bookingsData.data.allTransactions;
				}
				setHotelBookings(list);
			} catch (error) {
				console.error("Error while fetching dashboard data", error);
			} finally {
				setLoading(false);
			}
		};

		if (userName) {
			fetchDashboardData();
		}
	}, [userName]);

	const barChartRef = useRef(null);
	const donutChartRef = useRef(null);
	const barChartInstance = useRef(null);
	const donutChartInstance = useRef(null);

	useEffect(() => {
		if (loading) return;

		const checkAndInitCharts = () => {
			if (window.ApexCharts) {
				if (barChartInstance.current) {
					barChartInstance.current.destroy();
				}
				if (donutChartInstance.current) {
					donutChartInstance.current.destroy();
				}

				// 1. Group hotel bookings by date (day-wise)
				const tempGroup = {};
				hotelBookings.forEach((booking) => {
					const dateStr = booking.createdAt || booking.CreatedAt || booking.bookingDate || booking.BookingDate;
					if (dateStr) {
						const dateObj = new Date(dateStr);
						if (!isNaN(dateObj.getTime())) {
							const yyyymmdd = dateObj.toISOString().split("T")[0];
							tempGroup[yyyymmdd] = (tempGroup[yyyymmdd] || 0) + 1;
						}
					}
				});

				// Sort chronologically by YYYY-MM-DD
				const sortedKeys = Object.keys(tempGroup).sort();
				const chartDates = sortedKeys.map((key) => {
					const d = new Date(key);
					return d.toLocaleDateString("en-GB", {
						day: "2-digit",
						month: "short",
					});
				});
				const chartCounts = sortedKeys.map((key) => tempGroup[key]);

				// Area Chart Option (Day-wise Hotel Bookings)
				const areaOptions = {
					series: [
						{
							name: "Hotel Bookings",
							data: chartCounts.length > 0 ? chartCounts : [0],
						},
					],
					chart: {
						type: "area",
						height: 350,
						toolbar: {
							show: false,
						},
						fontFamily: "inherit",
					},
					stroke: {
						curve: "smooth",
						width: 3,
					},
					colors: ["#110942"], // Matching the dark premium theme color
					fill: {
						type: "gradient",
						gradient: {
							shadeIntensity: 1,
							opacityFrom: 0.45,
							opacityTo: 0.05,
							stops: [50, 100, 100],
						},
					},
					xaxis: {
						categories: chartDates.length > 0 ? chartDates : ["No Bookings"],
						axisBorder: {
							show: false,
						},
						axisTicks: {
							show: false,
						},
					},
					yaxis: {
						title: {
							text: "Bookings",
						},
						labels: {
							formatter: function (val) {
								return Math.round(val);
							},
						},
					},
					grid: {
						borderColor: "#f1f1f1",
						strokeDashArray: 4,
					},
					tooltip: {
						y: {
							formatter: function (val) {
								return val + " Bookings";
							},
						},
					},
				};

				barChartInstance.current = new window.ApexCharts(barChartRef.current, areaOptions);
				barChartInstance.current.render();

				// 2. Group hotel bookings by status (for donut chart)
				const statusCounts = {};
				hotelBookings.forEach((booking) => {
					const status = (booking.bookingStatus || booking.BookingStatus || "Unknown").trim();
					let normalizedStatus = status.toUpperCase();
					if (normalizedStatus.includes("CONFIRMED") || normalizedStatus.includes("SUCCESS")) {
						normalizedStatus = "Confirmed";
					} else if (normalizedStatus.includes("PENDING")) {
						normalizedStatus = "Pending";
					} else if (normalizedStatus.includes("CANCEL")) {
						normalizedStatus = "Cancelled";
					} else if (normalizedStatus.includes("HOLD")) {
						normalizedStatus = "On Hold";
					} else if (normalizedStatus.includes("FAIL")) {
						normalizedStatus = "Failed";
					} else {
						normalizedStatus = status.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
					}
					statusCounts[normalizedStatus] = (statusCounts[normalizedStatus] || 0) + 1;
				});

				const statusLabels = Object.keys(statusCounts);
				const statusValues = Object.values(statusCounts);

				const donutOptions = {
					series: statusValues.length > 0 ? statusValues : [1],
					chart: {
						type: "donut",
						height: 350,
						fontFamily: "inherit",
					},
					labels: statusLabels.length > 0 ? statusLabels : ["No Bookings"],
					colors: [
						"#50cd89", // Confirmed (Success/Green)
						"#ffc700", // On Hold / Pending (Warning/Yellow)
						"#f1416c", // Cancelled (Danger/Red)
						"#7239ea", // Other (Primary/Purple)
						"#009ef7", // Info/Blue
					],
					plotOptions: {
						pie: {
							donut: {
								size: "70%",
								labels: {
									show: true,
									total: {
										show: true,
										label: statusValues.length > 0 ? "Total Bookings" : "No Bookings",
										formatter: function (w) {
											if (statusValues.length === 0) return "0";
											return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
										},
									},
								},
							},
						},
					},
					legend: {
						position: "bottom",
					},
					tooltip: {
						enabled: statusValues.length > 0,
					},
				};

				donutChartInstance.current = new window.ApexCharts(donutChartRef.current, donutOptions);
				donutChartInstance.current.render();
			} else {
				setTimeout(checkAndInitCharts, 100);
			}
		};

		checkAndInitCharts();

		return () => {
			if (barChartInstance.current) {
				barChartInstance.current.destroy();
			}
			if (donutChartInstance.current) {
				donutChartInstance.current.destroy();
			}
		};
	}, [hotelBookings, loading]);


	return (
		<>
			{loading && (
				<p className="text-center load"><span className="loader"></span></p>
			)}
			<HeaderDashboard></HeaderDashboard>
			<div className="toolbar py-2" id="kt_toolbar">
				<div id="kt_toolbar_container" className="container-fluid d-flex align-items-center">
					<div className="flex-grow-1 flex-shrink-0 me-5">
						<div data-kt-swapper="true" data-kt-swapper-mode="prepend" data-kt-swapper-parent="{default: '#kt_content_container', 'lg': '#kt_toolbar_container'}" className="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
							<h1 className="d-flex align-items-center text-gray-900 fw-bold my-1 fs-3">Dashboard</h1>
							<span className="h-20px border-gray-200 border-start mx-3"></span>
							<ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1">
								<li className="breadcrumb-item">
									<a href="/Settings/Bands" className="text-muted text-hover-primary">Dashboard</a>
								</li>

							</ul>
						</div>
					</div>

				</div>
			</div>

			<section className='wraper'>
				<div className='box_shadow_d mt-16'>

					<div id="kt_content_container" >
						<div className="row">
							<div className="col-xl-3">
								<a href="/Reservations/Flight/AllTransactions" className="card bg-info hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.flightCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-plane text-white fs-2x ms-n1"></i>
										<div className="text-white fw-bold fs-2 mb-2 mt-5">Flight</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Hotel/AllTransactions" className="card bg-dark hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.hotelCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-hotel text-gray-100 fs-2x ms-n1"></i>
										<div className="text-gray-100 fw-bold fs-2 mb-2 mt-5">Hotel</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Bus/AllTransactions" className="card bg-warning hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.busCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-bus text-white fs-2x ms-n1"></i>
										<div className="text-white fw-bold fs-2 mb-2 mt-5">Bus</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Cab/AllTransactions" className="card bg-primary hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.cabCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-car text-white fs-2x ms-n1"></i>
										<div className="text-white fw-bold fs-2 mb-2 mt-5">Cabs</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Visa/AllTransactions" className="card bg-success hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.visaCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-address-card text-white fs-2x ms-n1"></i>
										<div className="text-white fw-bold fs-2 mb-2 mt-5">Visa</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Forex/AllTransactions" className="card bg-danger hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.forexCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-comments-dollar text-white fs-2x ms-n1"></i>
										<div className="text-white fw-bold fs-2 mb-2 mt-5">Forex</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Insurance/AllTransactions" className="card bg-primary-subtle hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.insuranceCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-umbrella text-primary-emphasis fs-2x ms-n1"></i>
										<div className="text-primary-emphasis fw-bold fs-2 mb-2 mt-5">Insurance</div>
									</div>
								</a>
							</div>
							<div className="col-xl-3">
								<a href="/Reservations/Train/AllTransactions" className="card bg-success-subtle hoverable card-xl-stretch mb-5">
									<div className="card-body">
										<div className="nry">{counts.trainCounts}</div>
										<img src="/assets/images/circle.png" alt="circle" className="card-img-absolute" />
										<i className="fa fa-train text-success-emphasis fs-2x ms-n1"></i>
										<div className="text-success-emphasis fw-bold fs-2 mb-2 mt-5">Rail</div>
									</div>
								</a>
							</div>


						</div>

						{/* Charts Section */}
						<div className="row mt-8">
							<div className="col-xl-6 mb-5">
								<div className="card card-xl-stretch shadow-sm" style={{ border: "1px solid #f1f1f1" }}>
									<div className="card-header align-items-center border-0 pt-5">
										<h3 className="card-title align-items-start flex-column">
											<span className="card-label fw-bold fs-3 text-gray-800">Daily Hotel Bookings</span>
											<span className="text-muted mt-1 fw-semibold fs-7">Day-wise count of hotel reservations</span>
										</h3>
									</div>
									<div className="card-body">
										<div ref={barChartRef} style={{ minHeight: "350px" }}></div>
									</div>
								</div>
							</div>
							<div className="col-xl-6 mb-5">
								<div className="card card-xl-stretch shadow-sm" style={{ border: "1px solid #f1f1f1" }}>
									<div className="card-header align-items-center border-0 pt-5">
										<h3 className="card-title align-items-start flex-column">
											<span className="card-label fw-bold fs-3 text-gray-800">Hotel Booking Status</span>
											<span className="text-muted mt-1 fw-semibold fs-7">Distribution by booking status</span>
										</h3>
									</div>
									<div className="card-body d-flex flex-column justify-content-center">
										<div ref={donutChartRef} style={{ minHeight: "350px" }}></div>
									</div>
								</div>
							</div>
						</div>

					</div>
				</div>
			</section>
		</>
	)
}

export default Dashboard;